/**
 * Consumer Domain Enums
 *
 * Enums for consumer portal statuses and types.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// CONSUMER STATUS
// ============================================================================

/**
 * Consumer account status
 */
export enum ConsumerStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

/** Zod schema for ConsumerStatus validation */
export const ConsumerStatusSchema = z.nativeEnum(ConsumerStatus);

/** Array of all consumer statuses for iteration */
export const CONSUMER_STATUSES = Object.values(ConsumerStatus);

// ============================================================================
// CONSUMER TYPE
// ============================================================================

/**
 * Consumer types for persona-based navigation and features.
 * Different consumer types see different dashboards and have different capabilities.
 */
export enum ConsumerType {
  /** LOS scenario - submit applications, upload documents, track status */
  LOAN_APPLICANT = 'LOAN_APPLICANT',
  /** LOS scenario - submit deals on behalf of clients, manage portfolio */
  CHANNEL_PARTNER = 'CHANNEL_PARTNER',
  /** Support/CRM scenario - submit cases, view products, self-service */
  END_CONSUMER = 'END_CONSUMER',
  /** CRM scenario - track referrals, view commissions */
  REFERRAL_PARTNER = 'REFERRAL_PARTNER',
}

/** Zod schema for ConsumerType validation */
export const ConsumerTypeSchema = z.nativeEnum(ConsumerType);

/** Array of all consumer types for iteration */
export const CONSUMER_TYPES = Object.values(ConsumerType);

/** Default consumer type when not specified */
export const DEFAULT_CONSUMER_TYPE = ConsumerType.END_CONSUMER;

// ============================================================================
// COMMISSION STATUS (Consumer Portal)
// ============================================================================

/**
 * Commission status for consumer portal display.
 * Note: This is a simplified status for consumer-facing views.
 * For internal commission tracking, see commission.enums.ts
 */
export enum ConsumerCommissionStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  APPROVED = 'approved',
  PAID = 'paid',
  CANCELLED = 'cancelled',
}

/** Zod schema for ConsumerCommissionStatus validation */
export const ConsumerCommissionStatusSchema = z.nativeEnum(ConsumerCommissionStatus);

/** Array of all consumer commission statuses for iteration */
export const CONSUMER_COMMISSION_STATUSES = Object.values(ConsumerCommissionStatus);

// ============================================================================
// REFERRAL STATUS
// ============================================================================

/**
 * Referral lifecycle status
 */
export enum ReferralStatus {
  /** Referral just submitted */
  NEW = 'new',
  /** Referral submitted and recorded */
  SUBMITTED = 'submitted',
  /** Prospect has been contacted */
  CONTACTED = 'contacted',
  /** Prospect is qualified */
  QUALIFIED = 'qualified',
  /** At term sheet stage */
  TERM_SHEET = 'term_sheet',
  /** Referral converted to deal */
  CONVERTED = 'converted',
  /** Deal funded */
  FUNDED = 'funded',
  /** Referral lost/declined */
  LOST = 'lost',
  /** Referral declined */
  DECLINED = 'declined',
  /** In pending state */
  PENDING = 'pending',
}

/** Zod schema for ReferralStatus validation */
export const ReferralStatusSchema = z.nativeEnum(ReferralStatus);

/** Array of all referral statuses for iteration */
export const REFERRAL_STATUSES = Object.values(ReferralStatus);

/**
 * Subset of {@link ReferralStatus} values exposed as filter options in list
 * APIs and UI dropdowns. Kept narrower than the full lifecycle to match the
 * states partners actually filter on.
 */
export const REFERRAL_FILTER_STATUSES = [
  ReferralStatus.PENDING,
  ReferralStatus.CONTACTED,
  ReferralStatus.QUALIFIED,
  ReferralStatus.CONVERTED,
  ReferralStatus.LOST,
] as const;

export type ReferralFilterStatus = (typeof REFERRAL_FILTER_STATUSES)[number];

/** Zod schema for ReferralFilterStatus validation */
export const ReferralFilterStatusSchema = z.enum(REFERRAL_FILTER_STATUSES);
